import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import {
  getClubLocations,
  createClubLocation,
  updateClubLocation,
  deleteClubLocation,
  type ClubLocation,
  type WeeklySchedule,
} from "../../services/api";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { SectionCard } from "../../components/admin/AdminCard";
import toast from "react-hot-toast";

const defaultSchedule: WeeklySchedule = {
  monday: { open: "08:00", close: "20:00" },
  tuesday: { open: "08:00", close: "20:00" },
  wednesday: { open: "08:00", close: "20:00" },
  thursday: { open: "08:00", close: "20:00" },
  friday: { open: "08:00", close: "20:00" },
  saturday: { open: "09:00", close: "18:00" },
  sunday: { open: "", close: "", closed: true },
};

export default function ClubLocations() {
  const { t } = useTranslation();
  const { activeClubId } = useAuth();

  const [locations, setLocations] = useState<ClubLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingLocation, setEditingLocation] = useState<ClubLocation | null>(null);
  const [locationForm, setLocationForm] = useState({
    name: "",
    address: "",
    description: "",
    capacity: "",
    schedule: defaultSchedule as WeeklySchedule,
  });

  useEffect(() => {
    if (!activeClubId) return;

    (async () => {
      setLoading(true);
      try {
        const data = await getClubLocations(activeClubId);
        setLocations(data);
      } catch (error) {
        console.error("Failed to load locations:", error);
      }
      setLoading(false);
    })();
  }, [activeClubId]);

  const updateScheduleDay = (day: keyof WeeklySchedule, field: "open" | "close" | "closed", value: string | boolean) => {
    setLocationForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: value,
        },
      },
    }));
  };

  const handleCreateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeClubId || !locationForm.name) return;

    setSaving(true);
    try {
      const newLocation = await createClubLocation(activeClubId, {
        name: locationForm.name,
        address: locationForm.address || undefined,
        description: locationForm.description || undefined,
        capacity: locationForm.capacity ? parseInt(locationForm.capacity) : undefined,
        schedule: locationForm.schedule,
      });
      setLocations(prev => [...prev, newLocation]);
      setLocationForm({
        name: "",
        address: "",
        description: "",
        capacity: "",
        schedule: defaultSchedule,
      });
      toast.success(t("admin.locationCreated", "Locație creată cu succes"));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t("error.createFailed", "Crearea a eșuat"));
    }
    setSaving(false);
  };

  const handleUpdateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeClubId || !editingLocation || !locationForm.name) return;

    setSaving(true);
    try {
      const updated = await updateClubLocation(activeClubId, editingLocation.id, {
        name: locationForm.name,
        address: locationForm.address || undefined,
        description: locationForm.description || undefined,
        capacity: locationForm.capacity ? parseInt(locationForm.capacity) : undefined,
        schedule: locationForm.schedule,
      });
      setLocations(prev => prev.map(l => l.id === editingLocation.id ? updated : l));
      cancelEditLocation();
      toast.success(t("common.saved", "Modificări salvate cu succes"));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t("error.saveFailed", "Salvarea a eșuat"));
    }
    setSaving(false);
  };

  const handleDeleteLocation = async (id: string) => {
    if (!activeClubId) return;
    if (!window.confirm(t("admin.deleteLocationConfirm", "Sigur dorești să ștergi această locație?"))) return;

    try {
      await deleteClubLocation(activeClubId, id);
      setLocations(prev => prev.filter(l => l.id !== id));
      if (editingLocation?.id === id) {
        cancelEditLocation();
      }
      toast.success(t("admin.locationDeleted", "Locație ștearsă"));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t("error.deleteFailed", "Ștergerea a eșuat"));
    }
  };

  const openEditLocation = (loc: ClubLocation) => {
    setEditingLocation(loc);
    setLocationForm({
      name: loc.name,
      address: loc.address || "",
      description: loc.description || "",
      capacity: loc.capacity?.toString() || "",
      schedule: (loc.schedule as WeeklySchedule) || defaultSchedule,
    });
  };

  const cancelEditLocation = () => {
    setEditingLocation(null);
    setLocationForm({
      name: "",
      address: "",
      description: "",
      capacity: "",
      schedule: defaultSchedule,
    });
  };

  if (loading) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
      </ClubAdminLayout>
    );
  }

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
      <AdminPageHeader
        title={t("admin.locations", "Locații")}
        subtitle={t("clubLocations.subtitle", "Adaugă și gestionează locațiile de antrenament.")}
        icon={<ClubNavIcon id="locations" className="w-6 h-6" />}
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <SectionCard
            className="sticky top-6"
            title={editingLocation ? t("admin.editLocation", "Editează locația") : t("admin.addLocation", "Adaugă locație")}
            bodyPadded
          >
            <form onSubmit={editingLocation ? handleUpdateLocation : handleCreateLocation} className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("common.name", "Nume")} *</label>
                <input
                  type="text"
                  required
                  value={locationForm.name}
                  onChange={e => setLocationForm({ ...locationForm, name: e.target.value })}
                  placeholder={t("admin.locationNamePlaceholder", "Ex: Sala principală")}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("admin.address", "Adresă")}</label>
                <input
                  type="text"
                  value={locationForm.address}
                  onChange={e => setLocationForm({ ...locationForm, address: e.target.value })}
                  placeholder={t("admin.addressPlaceholder", "Ex: Str. Sportului 10")}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("common.description", "Descriere")}</label>
                <textarea
                  rows={2}
                  value={locationForm.description}
                  onChange={e => setLocationForm({ ...locationForm, description: e.target.value })}
                  placeholder={t("admin.locationDescPlaceholder", "Descriere scurtă...")}
                  className="w-full px-3 py-2 text-sm bg-white border resize-none border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("admin.capacity", "Capacitate")}</label>
                <input
                  type="number"
                  min="0"
                  value={locationForm.capacity}
                  onChange={e => setLocationForm({ ...locationForm, capacity: e.target.value })}
                  placeholder={t("admin.capacityPlaceholder", "Ex: 30")}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Weekly Schedule */}
              <div>
                <label className="block mb-3 text-sm font-medium text-slate-700">{t("admin.weeklySchedule", "Program săptămânal")}</label>
                <div className="space-y-2">
                  {(["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const).map((day) => {
                    const daySchedule = locationForm.schedule[day] || { open: "", close: "", closed: false };
                    const isClosed = daySchedule.closed;
                    return (
                      <div key={day} className="flex items-center gap-2">
                        <span className="w-16 text-xs font-medium capitalize text-slate-600">
                          {t(`days.${day}`, day.charAt(0).toUpperCase() + day.slice(1, 3))}
                        </span>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isClosed}
                            onChange={e => updateScheduleDay(day, "closed", e.target.checked)}
                            className="w-4 h-4 text-indigo-500 rounded border-slate-300 focus:ring-indigo-500"
                          />
                          <span className="text-xs text-slate-500">{t("admin.closed", "Închis")}</span>
                        </label>
                        {!isClosed && (
                          <>
                            <input
                              type="time"
                              value={daySchedule.open || ""}
                              onChange={e => updateScheduleDay(day, "open", e.target.value)}
                              className="flex-1 px-2 py-1.5 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <span className="text-slate-400">-</span>
                            <input
                              type="time"
                              value={daySchedule.close || ""}
                              onChange={e => updateScheduleDay(day, "close", e.target.value)}
                              className="flex-1 px-2 py-1.5 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-2">
                {editingLocation && (
                  <button
                    type="button"
                    onClick={cancelEditLocation}
                    className="flex-1 px-4 py-3 font-medium transition-colors bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving || !locationForm.name}
                  className="flex items-center justify-center flex-1 gap-2 px-6 py-3 font-medium text-white transition-colors bg-indigo-600 hover:bg-indigo-700 rounded-xl disabled:opacity-50"
                >
                  {saving ? <div className="w-5 h-5 border-2 border-white rounded-full border-t-transparent animate-spin"></div> : null}
                  {editingLocation ? t("common.save", "Salvează") : t("common.add", "Adaugă")}
                </button>
              </div>
            </form>
          </SectionCard>
        </div>
        <div>
          <SectionCard
            title={t("admin.locations", "Locații")}
            actions={<span className="text-sm text-slate-500">{locations.length} {t("common.total", "total")}</span>}
            bodyPadded
          >
            <div className="space-y-4">
              {locations.map((loc) => (
                <div key={loc.id} className="p-4 transition-all bg-slate-50 rounded-xl border border-slate-200 hover:border-indigo-300">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="p-2 mt-1 bg-indigo-50 rounded-lg flex-shrink-0">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-800">{loc.name}</h4>
                        {loc.address && <p className="text-sm text-slate-500 mt-0.5">{loc.address}</p>}
                        {loc.description && <p className="mt-1 text-sm text-slate-600">{loc.description}</p>}
                        <div className="flex flex-wrap gap-3 mt-2">
                          {loc.capacity && (
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              {t("admin.capacityOf", "Capacitate")}: {loc.capacity}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${loc.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                            {loc.is_active ? t("common.active", "Activ") : t("common.inactive", "Inactiv")}
                          </span>
                        </div>
                        {/* Schedule Display */}
                        {loc.schedule && (
                          <div className="pt-3 mt-3 border-t border-slate-200">
                            <p className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {t("admin.schedule", "Program")}
                            </p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                              {(["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const).map((day) => {
                                const dayData = (loc.schedule as WeeklySchedule)?.[day];
                                if (!dayData) return null;
                                return (
                                  <div key={day} className="flex justify-between text-xs">
                                    <span className="capitalize text-slate-500">{t(`days.${day}`, day.slice(0, 3))}</span>
                                    <span className={dayData.closed ? "text-rose-500" : "text-slate-700"}>
                                      {dayData.closed ? t("admin.closedShort", "Închis") : `${dayData.open || "?"} - ${dayData.close || "?"}`}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditLocation(loc)}
                        className="p-2 transition-colors rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                        title={t("common.edit", "Editează")}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteLocation(loc.id)}
                        className="p-2 transition-colors rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                        title={t("common.delete", "Șterge")}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {locations.length === 0 && (
                <div className="py-12 text-center">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100">
                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-slate-700">{t("admin.noLocationsTitle", "Nicio locație încă")}</h3>
                  <p className="text-slate-500">{t("admin.noLocations", "Adaugă prima locație de antrenament!")}</p>
                </div>
              )}
            </div>
          </SectionCard>
        </div>
      </div>
      </div>
    </ClubAdminLayout>
  );
}
